import { auth } from '../../firebase/firebaseSetup';
import {
    browserSessionPersistence,
    createUserWithEmailAndPassword,
    sendPasswordResetEmail,
    setPersistence,
    signInWithEmailAndPassword,
    signOut,
    User,
    UserCredential,
    updateProfile
} from 'firebase/auth';
import ErrorInterface from '../interfaces/errorInterface';
import UserService, { UserClass, UserInterface } from '../user';
import ProfileService, { ProfileClass } from '../profile';

export default class AuthService extends UserService {
    /**
     * Add user data from firestore to user credential from auth
     * @param userCredential user credential from firebase
     * @returns userClass
     */
    private getUserClass = async (auth: User): Promise<UserClass> => {
        const userData = (await this.get(auth.uid)) as unknown as UserInterface;
        const userClass = new UserClass({ ...userData, auth });
        return userClass;
    };

    /**
     * Create a new user with email and password
     * @param email User email∆
     * @param password User password
     * @returns User if found, error if not
     */
    createUser = async (
        name: string,
        email: string,
        password: string
    ): Promise<UserClass | ErrorInterface> => {
        const profileService = new ProfileService();
        let result: UserClass | ErrorInterface;
        try {
            await setPersistence(auth, browserSessionPersistence);
            const newUser = await createUserWithEmailAndPassword(
                auth,
                email,
                password
            );
            result = (await this.save(
                new UserClass({
                    auth: newUser.user,
                    id: newUser.user.uid,
                    email,
                    name
                })
            )) as UserClass;
            profileService.save(new ProfileClass(result));
        } catch (e: any) {
            console.error({ e });
            result = {
                error: true,
                msg: e.message,
                code: e.code.replace(/[\W]/g, '_')
            } as ErrorInterface;
        }
        return result;
    };

    /**
     * Login a user with email and password
     * @param email User email
     * @param password User password
     * @returns User if found, error if not
     */
    loginUser = async (
        email: string,
        password: string
    ): Promise<UserCredential | ErrorInterface> => {
        let result: UserCredential | ErrorInterface;
        try {
            await setPersistence(auth, browserSessionPersistence);
            result = await signInWithEmailAndPassword(auth, email, password);
        } catch (e: any) {
            result = {
                error: true,
                msg: e.message,
                code: e.code.replace(/[\W]/g, '_')
            } as ErrorInterface;
        }
        return result;
    };

    /**
     * Logout a user
     * @returns void
     */
    logoutUser = async (): Promise<void | ErrorInterface> => {
        let result: void | ErrorInterface;
        try {
            result = (await signOut(auth)) as void;
        } catch (e: any) {
            result = {
                error: true,
                msg: e.message,
                code: e.code.replace(/[\W]/g, '_')
            } as ErrorInterface;
        }
        return result;
    };

    /**
     * Check if user is logged in
     * @returns User if found, false if not
     */
    getAuthState = (cb: Function) => {
        auth.onAuthStateChanged(async (authUser) => {
            return authUser?.uid
                ? cb(await this.getUserClass(authUser))
                : cb({});
        });
    };

    /**
     * Update User display name
     * @param user firebase user
     * @param displayName the new display name
     * @returns Promise<void>
     */
    updateUserName = (user: User, displayName: string): Promise<void> => {
        return updateProfile(user, { displayName });
    };

    /**
     * Send password reset email
     * @param email User email
     * @returns Promise<void>
     */
    sendPasswordResetEmail = async (
        email: string
    ): Promise<void | ErrorInterface> => {
        let result: void | ErrorInterface;
        try {
            result = await sendPasswordResetEmail(auth, email);
        } catch (e: any) {
            console.error('Error sending password reset email', e);
            result = {
                error: true,
                msg: e.message,
                code: e.code.replace(/[\W]/g, '_')
            } as ErrorInterface;
        }
        return result;
    };
}
